import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DateTime } from 'luxon';

import { floorToSlot } from '../event-time';

export type StartOption = 'now' | 'today' | 'tomorrow' | 'strip' | 'picker';

interface DayChip { iso: string; label: string }

/**
 * The Starts sheet's content (plan §5): a strip of the next 14 days plus
 * "Pick a date" (the native picker, so past dates still work for hosts
 * collecting old photos), and the 15-minute time list with "Now" on top for
 * today. Two choices, so the sheet has a Done — the parent calls confirm().
 */
@Component({
  selector: 'app-start-sheet',
  templateUrl: './start-sheet.component.html',
  styleUrls: ['./start-sheet.component.scss'],
})
export class StartSheetComponent implements OnInit, AfterViewInit {
  /** yyyy-MM-dd */
  @Input() date = '';
  /** "8:15 PM" */
  @Input() time = '';
  @Input() timeOptions: string[] = [];

  @Output() picked = new EventEmitter<{ date: string; time: string; option: StartOption }>();

  @ViewChild('timeList') timeList?: ElementRef<HTMLElement>;
  @ViewChild('datePicker') datePicker?: ElementRef<HTMLInputElement>;

  days: DayChip[] = [];
  selectedDate = '';
  selectedTime = '';
  nowSlot = '';
  private usedPicker = false;
  private pickedNow = false;

  ngOnInit(): void {
    const today = DateTime.now().startOf('day');
    this.nowSlot = floorToSlot(DateTime.now());
    this.days = Array.from({ length: 14 }, (_, i) => {
      const d = today.plus({ days: i });
      return { iso: d.toFormat('yyyy-MM-dd'), label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : d.toFormat('ccc d') };
    });
    this.selectedDate = this.date || this.days[0].iso;
    this.selectedTime = this.time || this.nowSlot;
    this.pickedNow = this.isToday && this.selectedTime === this.nowSlot;
  }

  ngAfterViewInit(): void {
    // Open on the chosen time, not at midnight. Scroll the list itself —
    // scrollIntoView would also move the sheet while it slides in.
    setTimeout(() => {
      const list = this.timeList?.nativeElement;
      const el = list?.querySelector('.is-selected') as HTMLElement | null;
      if (list && el) list.scrollTop = el.offsetTop - list.clientHeight / 2 + el.offsetHeight / 2;
    }, 0);
  }

  get isToday(): boolean {
    return this.selectedDate === this.days[0]?.iso;
  }

  /** A date from the native picker that is not in the strip gets its own chip. */
  get offStripLabel(): string | null {
    if (this.days.some(d => d.iso === this.selectedDate)) return null;
    const d = DateTime.fromISO(this.selectedDate);
    return d.isValid ? d.toFormat('ccc d LLL yyyy') : null;
  }

  pickDay(iso: string): void {
    this.selectedDate = iso;
    this.usedPicker = false;
    if (!this.isToday && this.pickedNow) this.pickedNow = false;
  }

  openPicker(): void {
    const input = this.datePicker?.nativeElement;
    if (!input) return;
    try {
      (input as any).showPicker ? (input as any).showPicker() : input.click();
    } catch {
      input.focus();
    }
  }

  onPickerChange(value: string): void {
    if (!value) return;
    this.selectedDate = value;
    this.usedPicker = true;
  }

  pickNow(): void {
    this.selectedTime = this.nowSlot;
    this.pickedNow = true;
  }

  pickTime(slot: string): void {
    this.selectedTime = slot;
    this.pickedNow = false;
  }

  isNowSelected(): boolean {
    return this.isToday && this.pickedNow;
  }

  isSlotSelected(slot: string): boolean {
    return !this.isNowSelected() && this.selectedTime === slot;
  }

  confirm(): void {
    const option: StartOption = this.isNowSelected() ? 'now'
      : this.usedPicker ? 'picker'
        : this.selectedDate === this.days[0]?.iso ? 'today'
          : this.selectedDate === this.days[1]?.iso ? 'tomorrow'
            : 'strip';
    this.picked.emit({ date: this.selectedDate, time: this.selectedTime, option });
  }
}
